<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  routePlansApi, type RoutePlan, type ApplyPreview, type ApplyFinding, type SkippedDuplicate,
} from '@/api/route-plans';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// "Aplicar y crear turnos" confirmation with a dry run of the apply gate: blockers (red,
// Confirmar disabled), warnings (amber, informative) and duplicate-demand skips. A confirm
// that gets blocked server-side (the world changed since the preview) re-hydrates the
// findings and stays open — the plan remains completed, so the user can act and retry.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'applied': [skipped: SkippedDuplicate[]];
  'updated': [];
}>();

const { t } = useI18n();

const preview = ref<ApplyPreview | null>(null);
const loadingPreview = ref(false);
const busy = ref(false);
const errorText = ref('');

async function loadPreview() {
  loadingPreview.value = true;
  errorText.value = '';
  try {
    preview.value = await routePlansApi.applyPreview(props.routePlan.id);
  } catch {
    // The apply itself re-runs the same gate inside its lock, so a failed preview
    // degrades to the old confirm-only dialog instead of blocking the flow.
    preview.value = null;
    errorText.value = t('routePlan.board.previewError');
  } finally {
    loadingPreview.value = false;
  }
}

watch(() => props.modelValue, (open) => {
  if (open) loadPreview();
}, { immediate: true });

const HTTP_UNPROCESSABLE_CONTENT = 422;

type ApplyBlockedResponse = {
  response?: {
    status?: number,
    data?: { message?: string, blockers?: ApplyFinding[], warnings?: ApplyFinding[] },
  };
};

function applyBlocked(error: unknown): boolean {
  const response = (error as ApplyBlockedResponse).response;

  return response?.status === HTTP_UNPROCESSABLE_CONTENT &&
    response.data?.message === 'apply_blocked';
}

const EMPTY_PREVIEW: ApplyPreview = {
  blockers: [], warnings: [], skippedDuplicates: [], creatableCount: 0,
};

// The world changed between the preview and the confirm: show the fresh findings in place
// (the plan stayed completed server-side) and let the parent refetch the board behind us.
function rehydrateFindings(error: unknown) {
  const data = (error as ApplyBlockedResponse).response?.data ?? {};
  preview.value = {
    ...EMPTY_PREVIEW,
    ...preview.value,
    blockers: data.blockers ?? [],
    warnings: data.warnings ?? [],
  };
  errorText.value = t('routePlan.board.applyBlockedRefresh');
  emit('updated');
}

async function confirmApply() {
  if (busy.value) return;
  busy.value = true;
  errorText.value = '';
  try {
    const skipped = await routePlansApi.apply(props.routePlan.id);
    emit('applied', skipped);
    emit('update:modelValue', false);
  } catch (error) {
    if (applyBlocked(error)) rehydrateFindings(error);
    else errorText.value = t('routePlan.board.applyError');
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    :max-width="520"
    :title="t('routePlan.board.applyAndCreate')"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #default>
      <div
        v-if="loadingPreview"
        class="d-flex justify-center pa-4"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
      <template v-else>
        <p
          v-if="!preview?.blockers.length"
          class="mb-2"
        >
          {{ t('routePlan.board.applySummary', {
            count: preview?.creatableCount ?? routePlan.assignedCount,
          }) }}
        </p>
        <v-alert
          v-if="preview?.blockers.length"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          <div class="font-weight-medium">
            {{ t('routePlan.board.applyBlockedTitle') }}
          </div>
          <div class="text-body-2 mb-1">
            {{ t('routePlan.board.applyBlockedHint') }}
          </div>
          <ul class="ml-4">
            <li
              v-for="(blocker, index) in preview.blockers"
              :key="`blocker-${index}`"
            >
              {{ blocker.message }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-if="preview?.warnings.length"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          <div class="font-weight-medium">
            {{ t('routePlan.board.applyWarningsTitle') }}
          </div>
          <ul class="ml-4">
            <li
              v-for="(warning, index) in preview.warnings"
              :key="`warning-${index}`"
            >
              {{ warning.message }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-if="preview?.skippedDuplicates.length"
          type="warning"
          variant="tonal"
          density="compact"
        >
          {{ t('routePlan.board.applyDuplicateWarning', {
            count: preview.skippedDuplicates.length,
          }) }}
          <ul class="mt-1">
            <li
              v-for="entry in preview.skippedDuplicates"
              :key="entry.routePlanVisitId"
            >
              {{ entry.patientName }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          {{ errorText }}
        </v-alert>
      </template>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('routePlan.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="busy"
        :disabled="loadingPreview || (preview?.blockers.length ?? 0) > 0"
        @click="confirmApply"
      >
        {{ t('routePlan.confirm') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
